import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stripe Checkout redirects here on payment success. We sit and poll
// /auth/me until the `checkout.session.completed` webhook lands and flips
// subscription_status to "active", then forward into the app. This avoids
// the race where ProtectedRoute reads stale "none" from AuthContext and
// bounces the just-paid user back to the paywall.
const POLL_INTERVAL_MS = 1200;
const MAX_POLL_ATTEMPTS = 30;   // ~36s wall time before showing the manual-refresh panel

export default function BillingSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState('polling'); // 'polling' | 'timeout' | 'error'
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);

  const goToApp = async () => {
    // Make sure AuthContext.user is fresh before navigating so the
    // ProtectedRoute paywall guard sees subscription_status="active".
    try { await checkAuth(); } catch (e) {}
    navigate('/dashboard?billing=success', { replace: true });
  };

  useEffect(() => {
    const pollOnce = async () => {
      if (cancelledRef.current) return;
      attemptsRef.current += 1;
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data && data.subscription_status === 'active') {
            cancelledRef.current = true;
            goToApp();
            return;
          }
        }
      } catch (e) { /* swallow — keep polling */ }
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setState('timeout');
        return;
      }
      timerRef.current = setTimeout(pollOnce, POLL_INTERVAL_MS);
    };
    pollOnce();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retry = async () => {
    // One immediate /auth/me check, then either forward or keep showing
    // the timeout panel.
    try {
      const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data && data.subscription_status === 'active') {
          goToApp();
          return;
        }
      }
    } catch (e) {}
    // still not active — restart the poll loop one more time
    attemptsRef.current = 0;
    cancelledRef.current = false;
    setState('polling');
    const tick = async () => {
      attemptsRef.current += 1;
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data && data.subscription_status === 'active') {
            goToApp();
            return;
          }
        }
      } catch (e) {}
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) { setState('timeout'); return; }
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };
    tick();
  };

  return (
    <div className="min-h-screen bg-[#F7FAFB]" data-testid="billing-success">
      {/* Matches the PlanPicker top bar so the transition feels seamless. */}
      <header className="bg-white border-b border-[#E2E8ED]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <img src="/nivela-logo.png" alt="Nivela" className="h-7" />
        </div>
      </header>
      <main className="max-w-md mx-auto px-6 py-24 text-center">
        {state === 'polling' && (
          <>
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0D1830]">Payment received — activating your subscription…</h1>
            <p className="text-sm text-[#72A4BF] mt-2 leading-snug">
              We're confirming with Stripe. This usually takes a couple of seconds —
              your dashboard will open automatically.
            </p>
            <div className="mt-8 flex justify-center">
              <Loader2 className="w-6 h-6 text-[#1D4053] animate-spin" />
            </div>
          </>
        )}
        {state === 'timeout' && (
          <>
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-[#0D1830]">Still processing</h1>
            <p className="text-sm text-[#72A4BF] mt-2 leading-snug">
              Stripe is taking longer than usual to confirm payment. Your card has been
              charged — your account will activate shortly. Try refreshing in a moment.
            </p>
            <Button onClick={retry}
              className="mt-6 bg-[#0D1830] text-white rounded-full hover:opacity-90"
              data-testid="billing-success-retry">
              Refresh
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
